/**
 * Helpers für sprachbewusstes Lesen/Schreiben von Content-Block-Inhalten.
 *
 * Content-Blocks speichern Text entweder einsprachig (`"Text"`) oder
 * mehrsprachig als Objekt (`{ "de": "...", "en": "..." }`). Der Feldlisten-Editor
 * (C1) arbeitet immer auf EINER Sprache; diese Helfer extrahieren bzw. mergen
 * den Wert der aktiven Sprache, ohne die anderen Sprachen zu verlieren.
 */

import type { ContentBlockContent } from '@/types/content-blocks'

/** Text-Block-Typen, die der Feldlisten-Editor als Textarea editiert. */
export const TEXT_BLOCK_TYPES = ['title', 'short_description', 'description'] as const

function isLangObject(content: unknown): content is Record<string, string> {
  return !!content && typeof content === 'object' && !Array.isArray(content)
}

/** Liest den Text der aktiven Sprache (Fallback: erster vorhandener Wert). */
export function readLangText(content: unknown, lang: string): string {
  if (content == null) return ''
  if (typeof content === 'string') return content
  if (isLangObject(content)) {
    const val = content[lang]
    if (typeof val === 'string') return val
    const first = Object.values(content).find((v) => typeof v === 'string')
    return typeof first === 'string' ? first : ''
  }
  return ''
}

/**
 * Mergt den neuen Text der aktiven Sprache zurück in die Block-Struktur.
 * Mehrsprachige Blocks behalten ihre übrigen Sprachen; einsprachige bleiben String.
 */
export function mergeLangText(content: unknown, lang: string, text: string): ContentBlockContent {
  if (isLangObject(content)) {
    // Mehrsprachige Blocks werden als { lang: text }-Objekt gespeichert
    // (vgl. ContentBlockCard) — Cast wie dort, da der Typ den Record nicht abbildet.
    return { ...content, [lang]: text } as unknown as ContentBlockContent
  }
  return text
}

/** Liest Bullet-Points als String-Liste (Array, lang→Array oder zeilengetrennter String). */
export function readBullets(content: unknown, lang: string): string[] {
  if (Array.isArray(content)) return content.filter((b): b is string => typeof b === 'string')
  if (isLangObject(content) && Array.isArray((content as Record<string, unknown>)[lang])) {
    return ((content as Record<string, unknown>)[lang] as unknown[]).filter((b): b is string => typeof b === 'string')
  }
  // Fallback: zeilengetrennter Text (auch lang-keyed {de: "a\nb"})
  const text = readLangText(content, lang)
  return text ? text.split('\n').filter(Boolean) : []
}

/** Verfügbare Sprach-Codes eines Blocks (leer = einsprachig). */
export function getBlockLanguages(content: unknown): string[] {
  if (isLangObject(content)) {
    return Object.keys(content).filter((k) => typeof content[k] === 'string')
  }
  return []
}
